import { useEffect, useMemo, useRef, useState } from 'react';
import { MultiSelect, RegistrationModalShell, SingleRangeSlider, TuitionModeSelector, WeeklyTimeTable } from '@tutor/ui';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { STATE_DISTRICTS, STATE_OPTIONS } from '@tutor/types';
import { getErrorMessage } from '@/api/errorHandler';
import { getMusicTutorProfile, submitMusicTutorProfile, updateMusicTutorProfile } from '@/api/services/musicProfilesApi';
import { updateTutorProfile } from '@/api/services/profilesApi';
import { TUITION_EXPERIENCE_YEARS } from '@/constants/dropdownOptions';
import { useAuthContext } from '@/contexts/useAuthContext';
import { useModal } from '@/contexts/useModal';
import { clearPendingRegistration, getPendingRegistration, savePendingRegistration } from '@/utils/storage';
import { parseMusicTutorStep } from '@/schemas/musicRegistrationSchemas';
import type { MusicPortfolioItemInput, MusicQualificationInput, MusicTutorRegistrationFormData } from '@/types/musicRegistration';
import TooltipIcon from '@/components/common/TooltipIcon';
import {
  MUSIC_AGE_GROUPS, MUSIC_EXAM_BOARDS, MUSIC_INSTRUMENTS, MUSIC_LESSON_FORMATS,
  MUSIC_LEVELS, MUSIC_PHYSICAL_VENUES, MUSIC_PORTFOLIO_TYPES, MUSIC_QUALIFICATION_TYPES, MUSIC_TEACHING_LANGUAGES,
} from '@/constants/musicRegistrationOptions';
import { CheckboxList, DropdownInput, Field, StepGrid, TextArea, TextInput } from './MusicFormControls';
import { useNavigate } from 'react-router-dom';

const steps = [
  { id: 'setup', label: 'Lesson setup' },
  { id: 'teaching', label: 'Teaching scope' },
  { id: 'qualifications', label: 'Professional qualifications' },
  { id: 'profile', label: 'Profile & review' },
];

const createEmptyQualification = (): MusicQualificationInput => ({ type: MUSIC_QUALIFICATION_TYPES[0], title: '' });
const createEmptyPortfolioItem = (): MusicPortfolioItemInput => ({ type: MUSIC_PORTFOLIO_TYPES[0], title: '', url: '' });

const initialData: MusicTutorRegistrationFormData = {
  instruments: [], musicLevels: [], ageGroups: [], tuitionType: '', lessonFormats: [], physicalVenues: [],
  preferredState: '', preferredDistricts: [], availability: {}, teachingExperienceYears: '', examBoards: [''],
  qualifications: [], portfolioItems: [], teachingLanguages: [], hourlyRate: 60, aboutMe: '', agreedToTerms: false,
};

const getErrors = (result: ReturnType<typeof parseMusicTutorStep>) => {
  if (result.success) return {};
  const fieldErrors: Record<string, string> = {};
  result.error.issues.forEach((issue) => {
    const field = String(issue.path[0]);
    if (field && !fieldErrors[field]) fieldErrors[field] = issue.message;
  });
  return fieldErrors;
};

interface MusicTutorRegistrationProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  presentation?: 'modal' | 'page';
  showPageBackButton?: boolean;
  successFeedback?: 'toast' | 'none';
}

const MusicTutorRegistrationModal = ({
  isOpen,
  onClose,
  onCompleted,
  presentation = 'modal',
  showPageBackButton,
  successFeedback = 'toast',
}: MusicTutorRegistrationProps) => {
  const navigate = useNavigate();
  const { backendUser, profile, updateProfile } = useAuthContext();
  const { openLoginModal, openCloseWarning } = useModal();
  const [data, setData] = useState(initialData);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const errorsRef = useRef<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldResumeSubmission, setShouldResumeSubmission] = useState(false);
  const isDirty = useMemo(() => JSON.stringify(data) !== JSON.stringify(initialData), [data]);
  const update = (values: Partial<MusicTutorRegistrationFormData>) => setData((current) => ({ ...current, ...values }));

  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  // Match Academic registration: only refresh errors already shown, and remove
  // each one as soon as that field becomes valid without revealing new errors.
  useEffect(() => {
    const currentErrors = errorsRef.current;
    if (Object.keys(currentErrors).length === 0) return;

    const result = parseMusicTutorStep(currentStep, data);
    const failingFields = getErrors(result);
    const refreshedErrors = Object.fromEntries(
      Object.keys(currentErrors)
        .filter((field) => failingFields[field])
        .map((field) => [field, failingFields[field]]),
    );

    if (JSON.stringify(refreshedErrors) !== JSON.stringify(currentErrors)) {
      setErrors(refreshedErrors);
    }
  }, [currentStep, data]);

  useEffect(() => {
    if (!isOpen) return;
    const pending = getPendingRegistration();
    if (pending?.type === 'music_tutor') {
      setData({
        ...initialData,
        ...pending.formData,
        examBoards: (pending.formData.examBoards as string[] | undefined)?.length ? pending.formData.examBoards as string[] : [''],
      } as MusicTutorRegistrationFormData);
      setCurrentStep(steps.length - 1);
      setShouldResumeSubmission(true);
      return;
    }
    if (!backendUser || profile?.role !== 'tutor') return;
    getMusicTutorProfile().then((music) => {
      if (!music) return;
      setData((current) => ({
        ...current,
        instruments: music.instruments ?? [], musicLevels: music.musicLevels ?? [], ageGroups: music.ageGroups ?? [],
        lessonFormats: music.lessonFormats ?? [], physicalVenues: music.physicalVenues ?? [], examBoards: music.examBoards?.length ? music.examBoards : [''],
        qualifications: music.qualifications ?? [], portfolioItems: music.portfolioItems ?? [],
        teachingExperienceYears: music.teachingExperienceYears ?? '', hourlyRate: music.hourlyRate ?? current.hourlyRate,
        tuitionType: profile.tuitionType ?? current.tuitionType, preferredState: profile.preferredState ?? '',
        preferredDistricts: profile.preferredDistricts ?? [], availability: profile.availability ?? {},
        teachingLanguages: profile.teachingLanguages ?? [], aboutMe: profile.aboutMe ?? '',
      }));
    }).catch(() => undefined);
  }, [backendUser, isOpen, profile]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!isOpen) {
      window.__devPanel?.unregister('music_tutor');
      return;
    }
    window.__devPanel?.register({
      type: 'music_tutor',
      setFormData: (value) => setData(value as MusicTutorRegistrationFormData),
      setCurrentStep,
      stepCount: steps.length,
      get currentStep() { return currentStep; },
    });
    return () => window.__devPanel?.unregister('music_tutor');
  }, [currentStep, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      window.__debugBridge?.unregisterFormData('music_tutor');
      return;
    }
    window.__debugBridge?.registerFormData('music_tutor', () => ({
      formData: data as unknown as Record<string, unknown>,
      validationErrors: errors,
      currentStep,
      stepCount: steps.length,
    }));
    return () => window.__debugBridge?.unregisterFormData('music_tutor');
  }, [currentStep, data, errors, isOpen]);

  const validate = () => {
    const result = parseMusicTutorStep(currentStep, data);
    setErrors(getErrors(result));
    return result.success;
  };

  const safeClose = () => {
    if (isDirty && !isSubmitting) {
      openCloseWarning({ title: 'Unsaved music profile', message: 'Discard the information entered in this form?', cancelText: 'Continue editing', confirmText: 'Discard', onConfirm: onClose });
      return;
    }
    onClose();
  };

  const submit = async () => {
    if (!validate()) return;
    if (backendUser && (profile?.role ?? backendUser.currentRole) !== 'tutor') {
      toast.error('Please switch to a Tutor account before submitting.');
      return;
    }
    if (!backendUser) {
      savePendingRegistration('music_tutor', data as unknown as Record<string, unknown>, presentation);
      if (presentation === 'page') {
        navigate('/music/register/auth');
        return;
      }
      onClose();
      openLoginModal({ initialMode: 'signup', initialUserType: 'tutor', fromRegistration: true, registrationService: 'music' });
      return;
    }
    setIsSubmitting(true);
    try {
      const qualifications = data.qualifications
        .filter((qualification) => qualification.title.trim())
        .map((qualification) => ({ ...qualification, title: qualification.title.trim() }));
      const portfolioItems = data.portfolioItems
        .filter((portfolioItem) => portfolioItem.title.trim())
        .map(({ url, instrument, ...portfolioItem }) => ({
          ...portfolioItem,
          title: portfolioItem.title.trim(),
          ...(url?.trim() ? { url: url.trim() } : {}),
          ...(instrument?.trim() ? { instrument: instrument.trim() } : {}),
        }));
      const baseProfile = await updateTutorProfile({
        tuitionType: data.tuitionType as 'physical' | 'online' | 'both',
        preferredState: data.tuitionType === 'online' ? undefined : data.preferredState,
        preferredDistricts: data.tuitionType === 'online' ? [] : data.preferredDistricts,
        availability: data.availability, teachingLanguages: data.teachingLanguages,
        aboutMe: data.aboutMe, agreedToTerms: data.agreedToTerms,
      });
      await updateMusicTutorProfile({
        instruments: data.instruments, musicLevels: data.musicLevels, ageGroups: data.ageGroups,
        lessonFormats: data.lessonFormats,
        physicalVenues: data.tuitionType === 'online' ? [] : data.physicalVenues,
        examBoards: data.examBoards.map((examBoard) => examBoard.trim()).filter(Boolean),
        qualifications, portfolioItems,
        teachingExperienceYears: Number(data.teachingExperienceYears), hourlyRate: data.hourlyRate,
      });
      await submitMusicTutorProfile();
      updateProfile({ ...baseProfile, role: 'tutor' });
      clearPendingRegistration();
      if (successFeedback === 'toast') {
        toast.success('Your Music Tutor profile has been submitted for review.');
      }
      setData(initialData);
      setCurrentStep(0);
      (onCompleted ?? onClose)();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const activeRole = profile?.role ?? backendUser?.currentRole;
    if (!shouldResumeSubmission || !backendUser || activeRole !== 'tutor' || isSubmitting) return;
    setShouldResumeSubmission(false);
    void submit();
  // The resume flag guarantees this runs once after authenticated state and restored data are ready.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUser, data, isSubmitting, profile, shouldResumeSubmission]);

  const qualificationItems = data.qualifications.length ? data.qualifications : [createEmptyQualification()];
  const portfolioItems = data.portfolioItems.length ? data.portfolioItems : [createEmptyPortfolioItem()];
  const addQualification = () => update({ qualifications: [...qualificationItems, createEmptyQualification()] });
  const addPortfolio = () => update({ portfolioItems: [...portfolioItems, createEmptyPortfolioItem()] });
  const addExamBoard = () => update({ examBoards: [...data.examBoards, ''] });

  return (
    <RegistrationModalShell isOpen={isOpen} onClose={safeClose} title="Become a Music Tutor" steps={steps} currentStep={currentStep} onBack={() => { setErrors({}); setCurrentStep((step) => Math.max(0, step - 1)); }} onNext={() => { if (validate()) { setErrors({}); setCurrentStep((step) => step + 1); } }} onSubmit={submit} isSubmitting={isSubmitting} submitLabel="Submit for review" themeClassName="theme-music" presentation={presentation} showPageBackButton={showPageBackButton}>
      {currentStep === 0 && <div className="space-y-6">
        <Field label="Lesson mode" error={errors.tuitionType}><TuitionModeSelector value={data.tuitionType} onChange={(tuitionType) => update({ tuitionType })} /></Field>
        <Field label="Lesson formats" error={errors.lessonFormats}><CheckboxList options={MUSIC_LESSON_FORMATS} value={data.lessonFormats} onChange={(values) => update({ lessonFormats: values as MusicTutorRegistrationFormData['lessonFormats'] })} /></Field>
        {data.tuitionType !== 'online' && <Field label="Physical venues (optional)" hint="Select any locations you can offer, or leave blank."><CheckboxList options={MUSIC_PHYSICAL_VENUES} value={data.physicalVenues} onChange={(physicalVenues) => update({ physicalVenues: physicalVenues as MusicTutorRegistrationFormData['physicalVenues'] })} /></Field>}
        {data.tuitionType !== 'online' && <section className="space-y-5 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900">Teaching location</h3>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Preferred state" error={errors.preferredState}><DropdownInput options={STATE_OPTIONS} value={data.preferredState} onChange={(value) => update({ preferredState: String(value), preferredDistricts: [] })} placeholder="Select state" /></Field>
            <Field label="Preferred districts" error={errors.preferredDistricts}><MultiSelect disabled={!data.preferredState} options={STATE_DISTRICTS[data.preferredState as keyof typeof STATE_DISTRICTS] ?? []} value={data.preferredDistricts} onChange={(preferredDistricts) => update({ preferredDistricts })} /></Field>
          </div>
        </section>}
      </div>}
      {currentStep === 1 && <div className="space-y-6">
        <StepGrid>
          <Field label="Instruments you teach" error={errors.instruments}><MultiSelect options={MUSIC_INSTRUMENTS} value={data.instruments} onChange={(instruments) => update({ instruments })} allowCustom /></Field>
          <Field label="Music levels" error={errors.musicLevels}><MultiSelect options={MUSIC_LEVELS} value={data.musicLevels} onChange={(musicLevels) => update({ musicLevels })} allowCustom /></Field>
          <Field label="Student age groups" error={errors.ageGroups}><MultiSelect options={MUSIC_AGE_GROUPS} value={data.ageGroups} onChange={(ageGroups) => update({ ageGroups })} allowCustom /></Field>
          <div className="space-y-2">
            <div className="flex min-h-7 items-center justify-between gap-2"><div className="flex items-center gap-2"><label className="text-sm font-semibold text-gray-800">Exam boards <span className="font-normal text-gray-500">(optional)</span></label><TooltipIcon label="What are exam boards?"><p>The music examination boards you can prepare students for, such as ABRSM, Trinity, or Rockschool.</p></TooltipIcon></div><button type="button" onClick={addExamBoard} className="inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-primary px-3 py-2 text-sm font-medium text-primary hover:bg-primary-50"><Plus className="h-4 w-4" /> Add another</button></div>
            <div className="space-y-3">{data.examBoards.map((examBoard, index) => <div key={index} className="flex items-start gap-2"><div className="min-w-0 flex-1"><DropdownInput options={MUSIC_EXAM_BOARDS} value={examBoard} onChange={(value) => update({ examBoards: data.examBoards.map((item, itemIndex) => itemIndex === index ? String(value) : item) })} placeholder="Select or enter an exam board" allowCustom /></div>{data.examBoards.length > 1 && <button type="button" aria-label="Remove exam board" onClick={() => update({ examBoards: data.examBoards.filter((_, itemIndex) => itemIndex !== index) })} className="min-h-11 min-w-11 text-red-600"><Trash2 className="mx-auto h-4 w-4" /></button>}</div>)}</div>
            {errors.examBoards && <p role="alert" className="text-sm font-medium text-red-600">{errors.examBoards}</p>}
          </div>
        </StepGrid>
        <div><WeeklyTimeTable selectedTimes={data.availability} onChange={(availability) => update({ availability })} label="Teaching availability" />{errors.availability && <p role="alert" className="mt-2 text-sm font-medium text-red-600">{errors.availability}</p>}</div>
      </div>}
      {currentStep === 2 && <div className="space-y-6">
        <Field label="Years teaching music" error={errors.teachingExperienceYears}><DropdownInput searchable={false} options={TUITION_EXPERIENCE_YEARS.map((option) => ({ value: option.value, label: option.label }))} value={data.teachingExperienceYears === '' ? '' : data.teachingExperienceYears} onChange={(value) => update({ teachingExperienceYears: Number(value) })} placeholder="Select years of experience" /></Field>
        <div className="flex items-center justify-between"><h3 className="font-semibold text-gray-900">Qualifications (optional)</h3><button type="button" onClick={addQualification} className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-primary px-3 py-2 text-sm font-medium text-primary hover:bg-primary-50"><Plus className="h-4 w-4" /> Add</button></div>
        {qualificationItems.map((item, index) => <div key={index} className="relative rounded-xl border border-gray-200 p-4"><button type="button" aria-label="Remove qualification" onClick={() => update({ qualifications: qualificationItems.length === 1 ? [] : qualificationItems.filter((_, itemIndex) => itemIndex !== index) })} className="absolute right-3 top-3 min-h-11 min-w-11 text-red-600"><Trash2 className="mx-auto h-4 w-4" /></button><div className="grid gap-4 pr-10 md:grid-cols-2"><DropdownInput searchable={false} options={MUSIC_QUALIFICATION_TYPES} value={item.type} onChange={(value) => update({ qualifications: qualificationItems.map((qualification, itemIndex) => itemIndex === index ? { ...qualification, type: String(value) } : qualification) })} /><TextInput placeholder="Qualification title" value={item.title} onChange={(event) => update({ qualifications: qualificationItems.map((qualification, itemIndex) => itemIndex === index ? { ...qualification, title: event.target.value } : qualification) })} /></div></div>)}
        {errors.qualifications && <p role="alert" className="text-sm font-medium text-red-600">{errors.qualifications}</p>}
        <div className="flex items-center justify-between"><div><h3 className="font-semibold text-gray-900">Professional portfolio (optional)</h3><p className="text-sm text-gray-600">Add YouTube, audio, or professional profile links.</p></div><button type="button" onClick={addPortfolio} className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-primary px-3 py-2 text-sm font-medium text-primary hover:bg-primary-50"><Plus className="h-4 w-4" /> Add</button></div>
        {portfolioItems.map((item, index) => <div key={index} className="relative rounded-xl border border-gray-200 p-4"><button type="button" aria-label="Remove portfolio item" onClick={() => update({ portfolioItems: portfolioItems.length === 1 ? [] : portfolioItems.filter((_, itemIndex) => itemIndex !== index) })} className="absolute right-3 top-3 min-h-11 min-w-11 text-red-600"><Trash2 className="mx-auto h-4 w-4" /></button><div className="grid gap-4 pr-10 md:grid-cols-2"><DropdownInput searchable={false} options={MUSIC_PORTFOLIO_TYPES} value={item.type} onChange={(value) => update({ portfolioItems: portfolioItems.map((portfolioItem, itemIndex) => itemIndex === index ? { ...portfolioItem, type: String(value) } : portfolioItem) })} /><TextInput placeholder="Title" value={item.title} onChange={(event) => update({ portfolioItems: portfolioItems.map((portfolioItem, itemIndex) => itemIndex === index ? { ...portfolioItem, title: event.target.value } : portfolioItem) })} /><TextInput type="url" placeholder="https://" value={item.url ?? ''} onChange={(event) => update({ portfolioItems: portfolioItems.map((portfolioItem, itemIndex) => itemIndex === index ? { ...portfolioItem, url: event.target.value } : portfolioItem) })} /><TextInput placeholder="Instrument (optional)" value={item.instrument ?? ''} onChange={(event) => update({ portfolioItems: portfolioItems.map((portfolioItem, itemIndex) => itemIndex === index ? { ...portfolioItem, instrument: event.target.value } : portfolioItem) })} /></div></div>)}
        {errors.portfolioItems && <p role="alert" className="text-sm font-medium text-red-600">{errors.portfolioItems}</p>}
      </div>}
      {currentStep === 3 && <div className="space-y-6">
        <Field label="Teaching languages" error={errors.teachingLanguages}><MultiSelect searchable={false} options={MUSIC_TEACHING_LANGUAGES} value={data.teachingLanguages} onChange={(teachingLanguages) => update({ teachingLanguages })} /></Field>
        <div className="space-y-2 rounded-xl border border-primary-200 bg-primary-50/70 p-4"><span className="text-sm font-semibold text-primary-800">Hourly rate <span className="text-red-500">*</span></span><SingleRangeSlider min={10} max={800} value={data.hourlyRate} onChange={(hourlyRate) => update({ hourlyRate })} unit="RM" colorTheme="primary" />{errors.hourlyRate && <p role="alert" className="text-sm font-medium text-red-600">{errors.hourlyRate}</p>}</div>
        <Field label="About your music teaching" error={errors.aboutMe} hint="Describe your approach, background, and what students can expect."><TextArea rows={5} maxLength={2000} value={data.aboutMe} onChange={(event) => update({ aboutMe: event.target.value })} /></Field>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-300 p-4"><input type="checkbox" checked={data.agreedToTerms} onChange={(event) => update({ agreedToTerms: event.target.checked })} className="mt-1 h-4 w-4 accent-primary" /><span className="text-sm text-gray-700">I agree to the <a href="/terms-tutors" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline hover:text-primary-600">Tutor Terms of Service</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline hover:text-primary-600">Privacy Policy</a>.</span></label>{errors.agreedToTerms && <p role="alert" className="text-sm text-red-600">{errors.agreedToTerms}</p>}
      </div>}
    </RegistrationModalShell>
  );
};

export default MusicTutorRegistrationModal;
